import { Tabs } from '@pms-digital/design-system';
import type { ReactElement, ReactNode } from 'react';

/**
 * The tab strip of the NPC section and of the fiche (npc.md §12, M3; maquette `Main`, `Fiche`):
 * « NPC 15 · Bancs 2 · Séries 6 », « Fiche · Rejeux 12 ».
 *
 * The package `Tabs` in its LINK form (`href` on every item): a tab that lives in the URL is a
 * destination, so each tab is a link, and the package draws it as a `role="tab"` inside a tablist
 * whose activation is manual (arrows move the focus, Enter navigates). The active tab is `active`,
 * which the caller reads off the URL. The links are built by the caller (`renderTab`) because
 * TanStack types a `<Link>` against the route tree, which a generic strip cannot know: the strip
 * hands it the classes and the label the package composed, and the package merges its tab props
 * onto the element it returns.
 *
 * The count is the number of rows the tab lists, in grey beside the label; it is omitted while it
 * is not known (a library still being read) and never shown as `0` for a tab nobody has read.
 */

export interface NpcTab<Id extends string> {
  readonly id: Id;
  readonly label: string;
  readonly count?: number | undefined;
}

export interface NpcTabStripProps<Id extends string> {
  /** The accessible name of the strip: « Sections du NPC », « Fiche du NPC ». */
  label: string;
  tabs: readonly NpcTab<Id>[];
  active: Id;
  /** The address a tab's `<a>` carries, search included (`/admin/npc?onglet=bancs`). */
  hrefOf: (tab: NpcTab<Id>) => string;
  /** The router's link of a tab, ONE element that forwards its props and its ref to the `<a>`. */
  renderTab: (tab: NpcTab<Id>, props: { className: string; children: ReactNode }) => ReactElement;
  'data-testid'?: string;
}

export function NpcTabStrip<Id extends string>({
  label,
  tabs,
  active,
  hrefOf,
  renderTab,
  'data-testid': testId = 'npc-onglets',
}: NpcTabStripProps<Id>) {
  // The package hands `renderLink` the address alone: the tab is found back from it.
  const byHref = new Map(tabs.map((tab) => [hrefOf(tab), tab]));
  return (
    <div data-testid={testId}>
      <Tabs
        label={label}
        value={active}
        items={tabs.map((tab) => ({
          value: tab.id,
          href: hrefOf(tab),
          label: (
            <span className="inline-flex items-center gap-1.5">
              {tab.label}
              {tab.count === undefined ? null : (
                <span className="font-normal text-gray-500 text-small">{tab.count}</span>
              )}
            </span>
          ),
        }))}
        renderLink={({ href, className, children }) => {
          const tab = byHref.get(href);
          return tab === undefined ? (
            <a href={href} className={className}>
              {children}
            </a>
          ) : (
            renderTab(tab, { className, children })
          );
        }}
      />
    </div>
  );
}
